import React from 'react'
import { State, PartialAction, Position } from 'src/types'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { SelectCardWithConfirm } from 'src/Components/Domain/SelectCardWithConfirm'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { useDispatcher } from 'src/features/atoms/glove/japan/logic/dispatchGenerator'
import { useLocale } from 'src/locales/useLocale'
import { BACK_STYLE_OPTION, CATCHER_MITT_BACK_STYLE_OPTION, WEB_PARTS_OPTION, webPartsObjs } from 'src/features/atoms/glove/constants/bases'
import {
  atomsFlagGenerator,
  baseCautionGenerator,
  dominantArmObjectSwitcher,
  fingerGuardTypeObjectSwitcher,
  tannerObjectWithConfirmSwitcher,
  webPartsFilter
} from 'src/features/atoms/glove/japan/logic/baseSetting'
import { positionChecker } from 'src/logic'
import { otherLabels } from '../constants/colors'
import { AGL_NE105_PRODUCT_NUMBER } from '../constants/baseModel'

type Props = {
  state: State
  selectedIndex: number
  position: Position
  isStaffOnly: boolean
  dispatch: React.Dispatch<PartialAction>
}

export const BaseSetter: React.FC<Props> = ({ state, selectedIndex, position, isStaffOnly, dispatch }) => {
  const { tanner, dominantArm, size, backLace, backStyle, materialPack, fingerGuardType, loopOfRingFinger, webParts, printLeatherType } = state
  const { t } = useLocale()
  const handle = {
    tanner: useDispatcher('tanner', dispatch, t),
    dominantArm: useDispatcher('dominantArm', dispatch, t),
    backStyle: useDispatcher('backStyle', dispatch, t),
    size: useDispatcher('size', dispatch, t),
    backLace: useDispatcher('backLace', dispatch, t),
    materialPack: useDispatcher('materialPack', dispatch, t),
    webParts: useDispatcher('webParts', dispatch, t),
    printLeatherType: useDispatcher('printLeatherType', dispatch, t),
    fingerGuardType: useDispatcher('fingerGuardType', dispatch, t),
    loopOfRingFinger: useDispatcher('loopOfRingFinger', dispatch, t)
  }
  const { isTannerSelected, isDefaultSize, isLeftThrow, isCatcherAndOtherThanAplSeries } = atomsFlagGenerator(state, position)
  const { isPitcher, isCatcher, isGlove } = positionChecker(position)
  const productNumber = state.baseModel.productNumber
  const isAGL_NE105 = productNumber === AGL_NE105_PRODUCT_NUMBER
  const isEmbroideryLabelSelected = otherLabels.some((x) => x.value === state.atomsLabel?.value)
  // 投手用 && バックスタイルが unselected → disabled
  const disabledFingerGuardType = isPitcher && backStyle?.value === 'unselected'
  const mustFilterBackStyle = isPitcher && !['unselected', 'none', 'indexCover'].includes(fingerGuardType?.value)

  return (
    <TabPanel selectedIndex={selectedIndex} index={0}>
      <SelectCardWithConfirm
        summary={t.ATOMS_LEATHER}
        selectedLabel={tanner?.label}
        selectedValue={tanner?.value}
        objects={tannerObjectWithConfirmSwitcher(t, state)}
        handleChange={handle.tanner}
        caution={baseCautionGenerator('tanner', t)}
        disabled={isTannerSelected && !isStaffOnly}
        isDisplay={!!state.kinds?.value}
        isError={state.tanner?.value === 'unselected'}
        defaultExpanded={tanner?.value === 'unselected'}
        description={isTannerSelected ? t.ATOMS_LEATHER_RESET_DESCRIPTION : ''}
        confirmDialogCaution={t.SELECT_CARD_WITH_CONFIRM_CAUTION_ONCE_SELECTED}
      />
      {isTannerSelected && (
        <>
          <SelectCard
            summary={t.DOMINANT_ARM}
            selectedLabel={dominantArm.label}
            objects={dominantArmObjectSwitcher(isCatcher, t)}
            handleChange={handle.dominantArm}
            description={isLeftThrow && baseCautionGenerator('dominantArm', t)}
            isError={dominantArm.value === 'unselected'}
          />
          <SelectCardWithImage
            summary={t.BACK_STYLE}
            selectedLabel={backStyle?.label}
            objects={
              mustFilterBackStyle ? t.BACK_STYLE_FOR_PITCHER_OBJECTS.filter((o) => o.value === backStyle?.value) : t.BACK_STYLE_FOR_PITCHER_OBJECTS
            }
            handleChange={handle.backStyle}
            isDisplay={isPitcher && !isAGL_NE105}
            isError={backStyle.value === 'unselected'}
            isShowLabel
            description={mustFilterBackStyle && t.CAUTION_BACK_STYLE}
            imageWidth={120}
            imageHeight={60}
            className={BACK_STYLE_OPTION}
          />
          <SelectCardWithImage
            summary={t.FINGER_GUARD}
            selectedLabel={fingerGuardType.label}
            objects={fingerGuardTypeObjectSwitcher(position, t, backStyle?.value)}
            handleChange={handle.fingerGuardType}
            disabled={disabledFingerGuardType}
            description={disabledFingerGuardType && t.FINGER_GUARD_TYPE_DESCRIPTION}
            imageHeight={80}
            imageWidth={140}
            isShowLabel
            isError={fingerGuardType.value === 'unselected'}
          />
          <SelectCardWithImage
            summary={t.MATERIAL_PACK}
            selectedLabel={materialPack?.label}
            objects={t.MATERIAL_PACK_OBJECTS}
            handleChange={handle.materialPack}
            isDisplay={isPitcher && !isAGL_NE105}
            imageWidth={160}
            imageHeight={80}
            isError={materialPack?.value === 'unselected'}
          />
          <SelectCard
            summary={t.FINGER_SIZE}
            selectedLabel={size.label}
            objects={t.SIZE_OBJECTS}
            handleChange={handle.size}
            caution={!isDefaultSize && baseCautionGenerator('size', t)}
            isDisplay={isGlove}
            isError={size.value === 'unselected'}
          />
          <SelectCardWithImage
            summary={t.BACK_LACE}
            selectedLabel={backLace?.label}
            objects={t.BACK_LACE_OBJECTS}
            handleChange={handle.backLace}
            isDisplay={isGlove}
            isShowLabel
            isError={backLace?.value === 'unselected'}
            imageWidth={200}
          />
          <SelectCardWithImage
            summary={t.WEB}
            selectedLabel={webParts?.label}
            objects={webPartsFilter(state?.baseModel.productNumber, position, webPartsObjs)}
            handleChange={handle.webParts}
            isDisplay={isGlove}
            isError={webParts?.value === 'unselected'}
            className={WEB_PARTS_OPTION}
          />
          <SelectCardWithImage
            summary={t.BACK_STYLE}
            selectedLabel={backStyle?.label}
            objects={t.BACK_STYLE_FOR_CATCHER_OBJECTS}
            handleChange={handle.backStyle}
            isDisplay={isCatcherAndOtherThanAplSeries}
            isError={backStyle?.value === 'unselected'}
            disabled={isEmbroideryLabelSelected}
            description={isEmbroideryLabelSelected ? '変更するには、先に L, M, N, O, P, Q, R, Z の中からラベルを再選択してください。' : ''}
            imageWidth={140}
            className={CATCHER_MITT_BACK_STYLE_OPTION}
          />
          <SelectCardWithImage
            summary={t.LOOP_OF_RING}
            selectedLabel={loopOfRingFinger?.label}
            objects={t.LOOP_OF_RING_FINGER_OBJECTS}
            description={t.LOOP_OF_RING_FINGER_DESCRIPTION}
            handleChange={handle.loopOfRingFinger}
            isDisplay={!isCatcher}
            isError={loopOfRingFinger?.value === 'unselected'}
            imageWidth={150}
            imageHeight={100}
            isShowLabel
          />
          <SelectCard
            summary={t.PRINT_LEATHER_TYPE}
            selectedLabel={printLeatherType?.label}
            objects={t.PRINT_LEATHER_TYPE_OBJECTS}
            isError={printLeatherType?.value === 'unselected'}
            handleChange={handle.printLeatherType}
            description={t.PRINT_LEATHER_TYPE_DESCRIPTION}
          />
        </>
      )}
    </TabPanel>
  )
}
